/**
 * The assistant wire protocol — PLAN §7.1.
 *
 * Shared by the Worker (which writes these frames) and the browser (which reads
 * them), exactly like `sync/protocol.ts`. Keeping it in one module is what stops
 * the two halves drifting: a new event type is a compile error on both sides at
 * once.
 *
 * `POST /api/ai/chat` answers with `text/event-stream`. Every frame is one SSE
 * `data:` line carrying a JSON {@link AiStreamEvent}. There are no event names —
 * the discriminant lives in the payload, so a reader needs one `switch` and no
 * knowledge of SSE naming rules.
 *
 * ```
 * data: {"type":"tool_activity","tool":"search_catalog","summary":"Searched catalog: “citra”"}
 * data: {"type":"text_delta","text":"Citra at whirlpool "}
 * data: {"type":"proposal", …}
 * data: {"type":"done","usage":{…}}
 * ```
 *
 * **The stream terminates with exactly one `done` or one `error`, for as long as
 * anyone is listening.** That is the client's contract for clearing its
 * "thinking…" state, and it holds for refusals, tool failures and API errors
 * alike — a hung stream is a bug, never a state the UI has to time out of. The
 * one case it cannot hold for is a reader that has gone: a browser that pressed
 * Stop, or a tab that was closed, has cancelled the body, so the Worker gets a
 * write failure instead of a listener and stops rather than narrating to nobody
 * (`worker/ai/routes.ts`). Nothing is owed to a client that left, and the client
 * that left already knows why — it says so itself, with a {@link STOPPED_NOTICE}.
 */

import type { Changeset } from '@beerwright/commands';

/** Where the assistant's routes live. */
export const AI_BASE_PATH = '/api/ai';

/** Assistant protocol version, sent on `/api/config` so a stale SPA can notice. */
export const AI_PROTOCOL_VERSION = 1;

/** How many messages a thread keeps before the oldest are pruned. */
export const THREAD_MESSAGE_LIMIT = 200;

/**
 * How much of a conversation the model is asked to remember, in characters.
 *
 * Every turn replays every surviving `model` row, and what makes that affordable
 * is prompt caching: the history is byte-stable from the front, so a long thread
 * is read at the cached rate instead of re-priced each message. A cap has to
 * keep that property, which is what rules out the obvious shape — a sliding
 * window of the last N messages drops a message off the front *every* turn,
 * moves the cached prefix every turn, and so makes long threads dearer rather
 * than cheaper. (A rolling summary is the other candidate and is parked: a
 * second model call, a summary that moves the prefix anyway, and a quiet way to
 * be wrong about someone's water.)
 *
 * So the cut is in blocks: nothing happens until the transcript passes
 * {@link MODEL_HISTORY_HIGH}, and then the oldest rows go until what is left is
 * at or under {@link MODEL_HISTORY_LOW}. The prefix moves once per block of
 * growth and is left alone in between. Sized in characters rather than rows
 * because characters are what the cost follows: two hundred rows of tool results
 * are not two hundred rows of "make it hoppier".
 *
 * Roughly sixteen thousand tokens down to ten — twenty-odd turns of a typical
 * recipe conversation, and a re-warm every eight or so. The facts that matter
 * are not in here anyway: the recipe as it stands, the open batch and the sheets
 * are rebuilt on every turn by `buildContextBlock` and were never history.
 */
export const MODEL_HISTORY_HIGH = 64_000;

/** …and what a trim leaves behind, so the prefix moves once per block, not per turn. */
export const MODEL_HISTORY_LOW = 40_000;

/** How many threads a workspace keeps before the least recently used are pruned. */
export const THREAD_LIMIT = 100;

// ---------------------------------------------------------------------------
// Stream events
// ---------------------------------------------------------------------------

/** Model usage for one chat turn, summed across every iteration of the loop. */
export interface AiUsage {
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  /** How many model round-trips the turn took. */
  readonly iterations: number;
}

/** Why a stream ended badly. */
export type AiErrorCode =
  /** The model declined the request (`stop_reason: "refusal"`). */
  | 'refusal'
  /** The daily message allowance is spent. */
  | 'rate_limited'
  /** No AI provider credential on this deployment. */
  | 'not_configured'
  /** The upstream API failed. */
  | 'upstream'
  /** The loop hit its iteration ceiling without finishing. */
  | 'exhausted'
  /** Anything else. */
  | 'internal';

/**
 * A staged changeset, plus what the client needs to detect staleness.
 *
 * Nothing here has been applied. `recipeUpdatedAt` is the recipe document's
 * modification time **as the assistant saw it**; if the recipe has moved since,
 * the diff card renders stale and offers a re-check rather than applying a
 * proposal computed against a workspace that no longer exists (PLAN §4.4).
 */
export interface AiProposal {
  readonly changeset: Changeset;
  /** The recipe the proposal targets, or `null` for a workspace-level change. */
  readonly recipeId: string | null;
  /** `recipes[recipeId].updatedAt` when the proposal was staged, or `null`. */
  readonly recipeUpdatedAt: number | null;
  /** True when the changeset's first command creates the recipe it targets. */
  readonly createsRecipe: boolean;
}

/** One frame of a chat stream. */
export type AiStreamEvent =
  /** A chunk of assistant prose. Concatenate in arrival order. */
  | { readonly type: 'text_delta'; readonly text: string }
  /** A tool ran. One line for the UI's activity chips. */
  | {
      readonly type: 'tool_activity';
      readonly tool: string;
      readonly summary: string;
      /** True when the tool returned `is_error`. */
      readonly failed: boolean;
    }
  /** A changeset was staged. Never applied — the user accepts it. */
  | { readonly type: 'proposal'; readonly proposal: AiProposal }
  /** The turn finished normally. */
  | { readonly type: 'done'; readonly usage: AiUsage; readonly threadId: string }
  /** The turn failed. Terminal. */
  | { readonly type: 'error'; readonly code: AiErrorCode; readonly message: string };

// ---------------------------------------------------------------------------
// Persisted transcript
// ---------------------------------------------------------------------------

/**
 * One rendered entry of a thread, as `GET /api/ai/threads/:id` returns it.
 *
 * This is the *UI* transcript, not the model's. The model's own messages (with
 * their `tool_use` / `tool_result` blocks) are stored alongside and never leave
 * the Worker — the browser has no use for them and every reason not to see the
 * raw tool plumbing.
 */
export type AiThreadEntryInput =
  | { readonly type: 'user'; readonly text: string }
  | { readonly type: 'assistant'; readonly text: string }
  | {
      readonly type: 'activity';
      readonly tool: string;
      readonly summary: string;
      readonly failed: boolean;
    }
  | { readonly type: 'proposal'; readonly proposal: AiProposal }
  /**
   * Something happened to the turn that is not something the assistant said.
   *
   * Two reasons, and the difference between them is who withdrew: `stopped` is
   * the brewer pressing Stop, `failed` is the turn ending in an `error` frame.
   * Both are drawn as a muted line rather than as a bubble, because neither is
   * an answer — an upstream failure stored as an ordinary assistant message
   * (which is what happened before this type existed) is both dishonest and
   * impossible to hang a retry on.
   *
   * `text` is the sentence to show: {@link STOPPED_NOTICE} for a stop, and the
   * error's own wording for a failure, so the drawer never has to guess at what
   * went wrong from a code.
   */
  | { readonly type: 'notice'; readonly reason: 'stopped' | 'failed'; readonly text: string };

/**
 * What a withdrawn turn says, on screen and in storage.
 *
 * Here rather than in either half because both write it: the browser puts it in
 * the transcript the moment the abort lands, and the Worker persists the same
 * sentence on the withdrawn turn, so re-opening the thread shows what the brewer
 * already read rather than a second wording of it.
 */
export const STOPPED_NOTICE = 'You stopped this answer. Ask again if you want the rest.';

/**
 * A persisted entry: what the turn produced, plus the identity storage gave it.
 *
 * Distributed over the union deliberately — `id` and `at` land on every member
 * rather than collapsing the union to its common keys, which is what a bare
 * `Omit`/intersection would do.
 */
export type AiThreadEntry = AiThreadEntryInput extends infer T
  ? T extends AiThreadEntryInput
    ? T & { readonly id: number; readonly at: number }
    : never
  : never;

/** A thread's identity, for the drawer's thread list. */
export interface AiThreadSummary {
  readonly threadId: string;
  /** The recipe this thread belongs to, or `null` for the global thread. */
  readonly recipeId: string | null;
  readonly title: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly messageCount: number;
}

/** `GET /api/ai/threads/:id`. */
export interface AiThreadResponse {
  readonly ok: true;
  readonly thread: AiThreadSummary;
  readonly entries: readonly AiThreadEntry[];
}

/** `GET /api/ai/threads?recipeId=…`. */
export interface AiThreadListResponse {
  readonly ok: true;
  readonly threads: readonly AiThreadSummary[];
}

/** `POST /api/ai/chat`. */
export interface AiChatRequest {
  /** Continue this thread, or start it if it does not exist yet. */
  readonly threadId: string;
  /** The recipe in view, so the assistant sees what the user sees. */
  readonly recipeId?: string | null;
  /** The blending session in view, when the user is on `/blending/:id`. */
  readonly blendId?: string | null;
  /**
   * The batch in view, on the batch page or its Feedback page.
   *
   * Trusted no more than `recipeId` is: it is a **pointer into the snapshot the
   * server already holds**, so the worst a wrong one can do is name a batch that
   * is not there. What it buys is a `get_batch` the assistant can make without
   * being told the id in prose (PLAN-SCORESHEETS S6).
   */
  readonly batchId?: string | null;
  readonly message: string;
}

// ---------------------------------------------------------------------------
// SSE framing
// ---------------------------------------------------------------------------

/** Serialise one event as an SSE frame, trailing blank line included. */
export function encodeSseEvent(event: AiStreamEvent): string {
  return `data: ${JSON.stringify(event)}\n\n`;
}

/**
 * Incremental SSE reader.
 *
 * Feed it decoded text chunks; it yields whole events. Written by hand rather
 * than pulled in as a dependency because the framing we emit is one `data:` line
 * per event and nothing else — comments, `event:` names and multi-line payloads
 * are not part of the protocol, so a hundred lines of general SSE parsing would
 * be dead code.
 */
export class SseDecoder {
  #buffer = '';

  /** Consume a chunk; returns the events it completed. */
  push(chunk: string): readonly AiStreamEvent[] {
    this.#buffer += chunk;
    const events: AiStreamEvent[] = [];
    for (;;) {
      const boundary = this.#buffer.indexOf('\n\n');
      if (boundary === -1) break;
      const frame = this.#buffer.slice(0, boundary);
      this.#buffer = this.#buffer.slice(boundary + 2);
      const decoded = decodeFrame(frame);
      if (decoded !== null) events.push(decoded);
    }
    return events;
  }
}

function decodeFrame(frame: string): AiStreamEvent | null {
  for (const line of frame.split('\n')) {
    if (!line.startsWith('data:')) continue;
    try {
      return JSON.parse(line.slice(5).trim()) as AiStreamEvent;
    } catch {
      return null;
    }
  }
  return null;
}
